import {
  ActionList,
  ActionListItem,
  Button,
  Checkbox,
  Loader,
  RadioGroup,
  SearchField
} from '@deque/cauldron-react';
import type { RadioItem } from '@deque/cauldron-react/lib/components/RadioGroup';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProducts } from '~/common/contexts/products';
import { deleteEnterpriseUsers } from '~/common/utils/admin-client/delete-users';
import billingClient, {
  memberHasAccepted,
  type EnterpriseMembers
} from '~/common/utils/billing-client/client-v2';
import ContentToast from '~/modules/common/components/ContentToast';
import styles from './DeleteUsersPanel.css';

type ActionType = 'remove_product_access' | 'remove_from_enterprise';

interface ConfirmViewProps {
  selectedMembers: EnterpriseMembers.AcceptedMember[];
  memberDisplayName: (member: EnterpriseMembers.AcceptedMember) => string;
}

interface InitialConfirmViewProps extends ConfirmViewProps {
  confirmText: string;
  onContinue: () => void;
  onCancel: () => void;
}

const InitialConfirmView: React.FC<InitialConfirmViewProps> = ({
  confirmText,
  selectedMembers,
  memberDisplayName,
  onContinue,
  onCancel
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.deleteUsersPanel}>
      <p>{confirmText}</p>
      <div className={styles.userList}>
        {selectedMembers.map(member => (
          <div key={member.user_id} className={styles.confirmationListItem}>
            <span className={styles.userName}>{memberDisplayName(member)}</span>
            <span className={styles.userEmail}>{member.email}</span>
          </div>
        ))}
      </div>
      <div className={styles.confirmActions}>
        <Button variant="primary" onClick={onContinue}>
          {t('Continue')}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          {t('Cancel')}
        </Button>
      </div>
    </div>
  );
};

interface FinalConfirmViewProps extends ConfirmViewProps {
  confirmText: string;
  actionButtonText: string;
  onConfirm: () => void;
  onGoBack: () => void;
}

const FinalConfirmView: React.FC<FinalConfirmViewProps> = ({
  confirmText,
  actionButtonText,
  selectedMembers,
  memberDisplayName,
  onConfirm,
  onGoBack
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.deleteUsersPanel}>
      <p>{confirmText}</p>
      <div className={styles.userList}>
        {selectedMembers.map(member => (
          <div key={member.user_id} className={styles.confirmationListItem}>
            <span className={styles.userName}>{memberDisplayName(member)}</span>
            <span className={styles.userEmail}>{member.email}</span>
          </div>
        ))}
      </div>
      <div className={styles.confirmActions}>
        <Button variant="error" onClick={onConfirm}>
          {actionButtonText}
        </Button>
        <Button variant="secondary" onClick={onGoBack}>
          {t('Go Back')}
        </Button>
      </div>
    </div>
  );
};

interface DeleteUsersPanelProps {
  members: EnterpriseMembers.Member[];
  enterpriseId: string;
  token: string;
  onComplete: () => void;
  onClose: () => void;
}

const DeleteUsersPanel: React.FC<DeleteUsersPanelProps> = ({
  members,
  enterpriseId,
  token,
  onComplete,
  onClose
}) => {
  const { t } = useTranslation();
  const { getProductBySlug } = useProducts();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showInitialConfirm, setShowInitialConfirm] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [actionType, setActionType] = useState<ActionType>(
    'remove_from_enterprise'
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
    },
    []
  );

  const acceptedMembers = useMemo(
    () => members.filter(memberHasAccepted),
    [members]
  );

  const availableProductSlugs = useMemo(() => {
    const slugs = new Set<string>();
    acceptedMembers.forEach(member => {
      member.product_access.forEach(pa => slugs.add(pa.product_slug));
    });
    return Array.from(slugs).sort();
  }, [acceptedMembers]);

  const [selectedProductSlugs, setSelectedProductSlugs] = useState<Set<string>>(
    () => new Set(availableProductSlugs)
  );

  useEffect(() => {
    setSelectedProductSlugs(new Set(availableProductSlugs));
  }, [availableProductSlugs]);

  const filteredMembers = useMemo(() => {
    if (!searchQuery) {
      return acceptedMembers;
    }
    const query = searchQuery.toLowerCase();
    return acceptedMembers.filter(
      member =>
        member.email.toLowerCase().includes(query) ||
        (member.first_name?.toLowerCase().includes(query) ?? false) ||
        (member.last_name?.toLowerCase().includes(query) ?? false)
    );
  }, [acceptedMembers, searchQuery]);

  const texts = useMemo(() => {
    switch (actionType) {
      case 'remove_product_access':
        return {
          initialConfirm: t(
            'Are you sure you want to proceed with removing product access for {{selectedCount}} user(s)?',
            { selectedCount: selectedIds.size }
          ),
          finalConfirm: t(
            'Are you sure you want to remove product access for {{selectedCount}} user(s)? This action cannot be undone.',
            { selectedCount: selectedIds.size }
          ),
          actionButton: t('Remove Product Access'),
          selectButton: t('Remove Product Access'),
          loaderLabel: t('Removing product access...'),
          successToast: t('Product access removed successfully')
        };
      case 'remove_from_enterprise':
      default:
        return {
          initialConfirm: t(
            'Are you sure you want to proceed with removing {{selectedCount}} user(s) from the enterprise?',
            { selectedCount: selectedIds.size }
          ),
          finalConfirm: t(
            'Are you sure you want to remove {{selectedCount}} user(s) from the enterprise? This action cannot be undone.',
            { selectedCount: selectedIds.size }
          ),
          actionButton: t('Remove from Enterprise'),
          selectButton: t('Remove from Enterprise'),
          loaderLabel: t('Removing users from enterprise...'),
          successToast: t('Users removed from enterprise successfully')
        };
    }
  }, [actionType, selectedIds.size, t]);

  const handleToggle = (userId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleProductToggle = (slug: string, checked: boolean) => {
    setSelectedProductSlugs(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(slug);
      } else {
        next.delete(slug);
      }
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    setConfirmingDelete(false);
    setDeleting(true);
    setDeleteError(null);

    try {
      if (actionType === 'remove_from_enterprise') {
        const keycloakIds = Array.from(selectedIds);
        const result = await deleteEnterpriseUsers({
          token,
          enterpriseId,
          keycloakIds
        });

        if (result.deleted > 0) {
          setSuccessMessage(texts.successToast);
          setSelectedIds(new Set());
          onComplete();
          clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => setSuccessMessage(null), 5000);
        }

        if (result.failures.length > 0) {
          const failedEmails = result.failures
            .map(f => {
              const member = acceptedMembers.find(
                m => m.user_id === f.keycloakId
              );
              return member?.email ?? f.keycloakId;
            })
            .join(', ');
          setDeleteError(
            t(
              'Failed to remove {{failedCount}} user(s) from the enterprise: {{emails}}',
              {
                failedCount: result.failures.length,
                emails: failedEmails
              }
            )
          );
        }
      } else if (actionType === 'remove_product_access') {
        const userIds = Array.from(selectedIds);
        const slugs = Array.from(selectedProductSlugs);
        const failedSlugs: string[] = [];

        for (const slug of slugs) {
          const product = getProductBySlug(slug);
          if (!product) {
            failedSlugs.push(slug);
            continue;
          }
          try {
            await billingClient.enterpriseMembers.bulkRemoveProductAccess({
              token,
              enterpriseId,
              keycloakIds: userIds,
              productId: product.id
            });
          } catch {
            failedSlugs.push(slug);
          }
        }

        const successCount = slugs.length - failedSlugs.length;

        if (successCount > 0) {
          setSuccessMessage(texts.successToast);
          setSelectedIds(new Set());
          onComplete();
          clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => setSuccessMessage(null), 5000);
        }

        if (failedSlugs.length > 0) {
          const failedProductNames = failedSlugs
            .map(s => getProductBySlug(s)?.name || s)
            .join(', ');
          setDeleteError(
            t(
              'Failed to remove access for {{failedCount}} product(s): {{products}}',
              {
                failedCount: failedSlugs.length,
                products: failedProductNames
              }
            )
          );
        }
      }
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  const memberDisplayName = (member: EnterpriseMembers.AcceptedMember) => {
    const name = [member.first_name, member.last_name]
      .filter(Boolean)
      .join(' ');
    return name || member.email;
  };

  const selectedMembers = useMemo(
    () => acceptedMembers.filter(member => selectedIds.has(member.user_id)),
    [acceptedMembers, selectedIds]
  );

  if (showInitialConfirm) {
    return (
      <InitialConfirmView
        confirmText={texts.initialConfirm}
        selectedMembers={selectedMembers}
        memberDisplayName={memberDisplayName}
        onContinue={() => {
          setShowInitialConfirm(false);
          setConfirmingDelete(true);
        }}
        onCancel={() => setShowInitialConfirm(false)}
      />
    );
  }

  if (confirmingDelete) {
    return (
      <FinalConfirmView
        confirmText={texts.finalConfirm}
        actionButtonText={texts.actionButton}
        selectedMembers={selectedMembers}
        memberDisplayName={memberDisplayName}
        onConfirm={handleDeleteSelected}
        onGoBack={() => setConfirmingDelete(false)}
      />
    );
  }

  return (
    <div className={styles.deleteUsersPanel}>
      <div className={styles.actionTypeSection}>
        <RadioGroup
          name="actionType"
          value={actionType}
          onChange={(radio: RadioItem) =>
            setActionType(radio.value as ActionType)
          }
          radios={[
            {
              label: t('Remove product access'),
              value: 'remove_product_access'
            },
            {
              label: t('Remove from enterprise'),
              value: 'remove_from_enterprise'
            }
          ]}
        />
      </div>

      {actionType === 'remove_product_access' &&
        availableProductSlugs.length > 0 && (
          <div className={styles.productPicker}>
            {availableProductSlugs.map(slug => (
              <Checkbox
                key={slug}
                id={`product-${slug}`}
                label={getProductBySlug(slug)?.name || slug}
                name={`product-${slug}`}
                checked={selectedProductSlugs.has(slug)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleProductToggle(slug, e.target.checked)
                }
              />
            ))}
          </div>
        )}

      <SearchField
        label={t('Search by name or email')}
        placeholder={t('Search by name or email')}
        hideLabel
        value={searchQuery}
        onChange={(value: string) => setSearchQuery(value)}
      />

      {deleting && <Loader label={texts.loaderLabel} tabIndex={-1} />}

      {!deleting && (
        <div className={styles.userList}>
          <ActionList selectionType="multiple">
            {filteredMembers.map(member => (
              <ActionListItem
                key={member.user_id}
                selected={selectedIds.has(member.user_id)}
                onAction={() => handleToggle(member.user_id)}
              >
                <span className={styles.userItem}>
                  <span className={styles.userName}>
                    {memberDisplayName(member)}
                  </span>
                  <span className={styles.userEmail}>{member.email}</span>
                </span>
              </ActionListItem>
            ))}
          </ActionList>
          {filteredMembers.length === 0 && (
            <p className={styles.emptyState}>{t('No users found')}</p>
          )}
        </div>
      )}

      <div className={styles.footer}>
        <span className={styles.selectedCount}>
          {t('{{selectedCount}} selected', { selectedCount: selectedIds.size })}
        </span>
        <div className={styles.footerActions}>
          <Button
            variant={
              actionType === 'remove_product_access' ? 'primary' : 'error'
            }
            disabled={
              selectedIds.size === 0 ||
              (actionType === 'remove_product_access' &&
                selectedProductSlugs.size === 0)
            }
            onClick={() => setShowInitialConfirm(true)}
          >
            {texts.selectButton}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
        </div>
      </div>

      {successMessage && (
        <ContentToast type="confirmation" show>
          {successMessage}
        </ContentToast>
      )}

      {deleteError && (
        <ContentToast type="caution" show>
          {deleteError}
        </ContentToast>
      )}
    </div>
  );
};

export default DeleteUsersPanel;
